import { useState } from 'react';
import { api, ApiError, newId } from '../api/client';
import type { Transaction } from '../api/types';
import { krw, kindLabel } from '../lib/format';
import { useApp } from '../state';

const ICON: Record<Transaction['kind'], string> = { income: '＋', expense: '↗', refund: '↺', transfer: '⇄' };

/** 내역 한 줄. actions=true면 수정·환불·삭제 버튼을 보여준다. */
export function TransactionRow({ tx, actions = false }: { tx: Transaction; actions?: boolean }) {
  const { openEntry, changed, toast, catalog } = useApp();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const accountName = (id: string | null) => catalog.accounts.find((a) => a.id === id)?.name ?? '계좌';
  const detail =
    tx.kind === 'transfer'
      ? `${accountName(tx.fromAccountId)} → ${accountName(tx.toAccountId)} · 합계 제외`
      : `${tx.categoryName ?? '-'} › ${tx.subcategoryName ?? '-'} · ${tx.paymentMethodName ?? '수단 미지정'}`;
  const positive = tx.kind === 'income' || tx.kind === 'refund';

  async function remove() {
    setBusy(true);
    setError(null);
    try {
      await api.delete(`/transactions/${tx.id}`, { ifMatch: tx.revision, idempotencyKey: newId() });
      toast('삭제했습니다.');
      changed();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '삭제하지 못했습니다.');
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="transaction">
      <span className="icon" aria-hidden>
        {ICON[tx.kind]}
      </span>
      <div className="details">
        <b>{tx.merchant || kindLabel[tx.kind]}</b>
        <small>
          {tx.effectiveDate} · {tx.kind !== 'expense' && tx.kind !== 'income' ? `${kindLabel[tx.kind]} · ` : ''}
          {detail}
          {tx.source === 'recurring' ? ' · 반복' : tx.source === 'import' ? ' · 가져옴' : ''}
          {tx.memo ? ` · ${tx.memo}` : ''}
        </small>
        {error && <small className="error">{error}</small>}
      </div>
      <span className={`price ${positive ? 'income-price' : ''}`}>
        {tx.kind === 'transfer' ? '' : positive ? '+' : '−'}
        {krw(tx.amountKrw)}
      </span>
      {actions &&
        (confirming ? (
          <span className="row-actions">
            <button className="delete danger" disabled={busy} onClick={() => void remove()}>
              삭제 확인
            </button>
            <button className="delete" onClick={() => setConfirming(false)}>
              취소
            </button>
          </span>
        ) : (
          <span className="row-actions">
            <button className="delete" onClick={() => openEntry({ edit: tx })}>
              수정
            </button>
            {tx.kind === 'expense' && (
              <button className="delete" onClick={() => openEntry({ refundOf: tx })}>
                환불
              </button>
            )}
            <button className="delete" onClick={() => setConfirming(true)} aria-label={`${tx.merchant ?? '기록'} 삭제`}>
              삭제
            </button>
          </span>
        ))}
    </div>
  );
}
